import { useCallback, useState } from 'react';
import { consultationService } from '../../../services/consultationService';
import { canPerformAction } from '../../../utils/permissions';
import { useAuth } from '../../../contexts/AuthContext';
import type { MedicalConsultation } from '../../../types/api';
import type { AuthUser } from '../../../services/authService';

/**
 * Status-derived visibility rules shared by ConsultationListPage,
 * ConsultationDetailPage and the print pages (medical-assistant-delegation
 * task 3.10 — consolidates what were previously separate ad-hoc checks).
 * Pure functions: no hook overhead where a component just needs a boolean.
 */

/** clinical-record-signing R6: signed and amended records are immutable. */
export function isConsultationReadOnly(consultation: Pick<MedicalConsultation, 'status'> | null | undefined): boolean {
  return !!consultation && consultation.status !== undefined && consultation.status !== 'draft';
}

/**
 * clinical-record-signing R3: while draft, the responsible doctor (and, by
 * extension, an assigned assistant — the backend is the real authority gate)
 * may edit. Once signed/amended, the only writable action left is
 * "Registrar resultado" (R15), which callers gate separately.
 */
export function canEditConsultation(consultation: Pick<MedicalConsultation, 'status'> | null | undefined): boolean {
  return !consultation || consultation.status === undefined || consultation.status === 'draft';
}

/**
 * clinical-record-signing R14: only the responsible doctor or the assistant
 * who recorded it (`created_by`), and only while still draft.
 */
export function canDeleteConsultation(
  consultation: Pick<MedicalConsultation, 'status' | 'doctor_id' | 'created_by'> | null | undefined,
  user: Pick<AuthUser, 'id' | 'doctor'> | null | undefined,
): boolean {
  if (!consultation || !user) return false;
  if (consultation.status !== undefined && consultation.status !== 'draft') return false;
  const isResponsibleDoctor = !!user.doctor && user.doctor.id === consultation.doctor_id;
  const isRecorder = consultation.created_by !== undefined && consultation.created_by === user.id;
  return isResponsibleDoctor || isRecorder;
}

/** The message to show when signing fails (409 stale version, else the backend's reason). */
export function signErrorMessage(err: any): string {
  if (err?.response?.status === 409) return 'El registro fue modificado por otro usuario. Recargue antes de firmar.';
  return err?.response?.data?.message || 'Error al firmar la consulta';
}

/**
 * Shared "Firmar consulta" behavior (design.md Frontend section). The
 * responsible doctor signs any draft of theirs ('reviewed'). An assistant
 * signs only a draft they recorded, for a doctor whose delegated signature
 * is in effect right now (`delegatedSignerDoctorIds`, from my-doctors'
 * flag-aware `can_sign_delegated`) — clinical-record-signing R12. The
 * backend re-checks all of it at sign time.
 */
export function useCanSign(
  consultation: MedicalConsultation | null | undefined,
  onSigned?: (updated: MedicalConsultation) => void,
  delegatedSignerDoctorIds: number[] = [],
) {
  const { user } = useAuth();
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isResponsibleDoctor = !!user?.doctor && !!consultation && user.doctor.id === consultation.doctor_id;
  const isDelegatedSigner = !!user && !!consultation && consultation.created_by === user.id
    && delegatedSignerDoctorIds.includes(consultation.doctor_id);
  const hasSignAction = !!user && canPerformAction(user, 'consultations', 'sign');
  const canSign = !!consultation && consultation.status === 'draft' && (isResponsibleDoctor || isDelegatedSigner) && hasSignAction;

  const sign = useCallback(async () => {
    if (!consultation?.id || consultation.lock_version === undefined) return;
    setSigning(true);
    setError(null);
    try {
      const updated = await consultationService.sign(consultation.id, consultation.lock_version);
      onSigned?.(updated);
      return updated;
    } catch (err: any) {
      setError(signErrorMessage(err));
      throw err;
    } finally {
      setSigning(false);
    }
  }, [consultation?.id, consultation?.lock_version, onSigned]);

  return { canSign, signing, error, sign };
}
